<template>
  <Slideover :visible="visible" :size="size">
    <AddNote
      title="Add Note"
      :visible="slideoverVisibleAddNote"
      v-if="slideoverVisibleAddNote"
      v-on:toggle-slideover="toggleAddNoteSlideover('max-w-3xl')"
      v-on:notes-changed="loadReport"
      :orderNumber="orderNumber"
    />
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div class="py-3 px-2 mx-4 flex flex1 flex-row justify-between text-sm">
      <div class="pb-1 w-4/5">
        <span v-if="selectedNote">{{ selectedNoteText }}</span>
        <span v-else>Select a note to view its contents here...</span>
      </div>
      <div class="relative inline-block text-left z-10">
        <Button
          v-on:click-handler="toggleAddNoteSlideover('max-w-xl')"
          class="btn btn-primary btn-small flex space-x-2 items-center"
        >
          <span>Add Note</span>
        </Button>
      </div>
    </div>
    <div class="relative flex-1 py-3 px-2 mx-4">
      <ag-grid-vue
        style="width: 100%; height: 95%"
        class="ag-theme-balham"
        :columnDefs="columnDefs"
        :rowData="rowData"
        id="orderNotes"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
        :context="context"
        :frameworkComponents="frameworkComponents"
        :defaultColDef="defaultColDef"
        :paginationAutoPageSize="false"
        :paginationPageSize="paginationPageSize"
        :pagination="true"
        :enableCellTextSelection="true"
        :ensureDomOrder="true"
      >
      </ag-grid-vue>
    </div>

    <HttpError :error="error" />

    <!-- Footer -->
  </Slideover>
</template>
<script>
import Slideover from "@/components/layout/Slideover.vue";
import moneyFormatter from "@/mixins/moneyFormatter";
import { AgGridVue } from "ag-grid-vue";
import { datetime } from "@/components/agGrid/cellRenderers";
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button";
import AddNote from "@/views/Customers/components/slideovers/AddNote";
const NotesApi = ApiFactory.get("notes");

export default {
  name: "OrderNotes",
  data() {
    return {
      error: null,
      columnDefs: null,
      rowData: null,
      pagination: false,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      notes: {},
      paginationPageSize: 50,
      selectedNoteText: "",
      selectedNote: false,
      slideoverVisibleAddNote: false,
      size: "max-w-3xl",
    };
  },
  props: {
    orderNumber: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  created() {},
  components: {
    AddNote,
    Button,
    Slideover,
    AgGridVue,
  },
  computed: {
    filteredNotes() {
      let occurrences = {};

      return this.notes
        .filter((note) => {
          let identifier = `${note?.ItemNumber}_${note?.AlphaKey}`;

          if (
            !Object.prototype.hasOwnProperty.call(
              occurrences,
              `${identifier}-purchase`
            )
          ) {
            occurrences[`${identifier}-purchase`] = 0;
          }
          if (
            !Object.prototype.hasOwnProperty.call(
              occurrences,
              `${identifier}-care`
            )
          ) {
            occurrences[`${identifier}-care`] = 0;
          }

          if (note.Keywords?.includes("backorder-tool:purchase-note")) {
            return !occurrences[`${identifier}-purchase`]++;
          }

          if (note.Keywords?.includes("backorder-tool:care-note")) {
            return !occurrences[`${identifier}-care`]++;
          }

          return true;
        })
        .map((note) => {
          if (note.Keywords?.includes("backorder-tool:purchase-note")) {
            note.Notes = `Purchase Note (${note.AlphaKey}): ${note.Notes}`;
          }

          if (note.Keywords?.includes("backorder-tool:care-note")) {
            note.Notes = `Care Note (${note.AlphaKey}): ${note.Notes}`;
          }

          if (note?.action_type_id) {
            note.Notes = this.buildNote(note);
          }

          return note;
        });
    },
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      rowSelection: "single",
      onSelectionChanged: this.onNoteSelectionChanged,
      rowDeselection: true,
      getRowStyle(params) {
        if (params.data.AssignedTo !== null && params.data.Status === null) {
          return { "background-color": "#0060fb", color: "white" };
        }
      },
      onRowClicked: (params) => {
        if (params.node.data.Notes) {
          this.selectedNoteText = params.node.data.Notes;
        } else {
          this.selectedNoteText = params.node.data.note;
        }
      },
    };

    this.context = { componentParent: this };
    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 0,
      suppressMovable: false,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Date",
        valueGetter: (params) => new Date(params.data.EntryDate),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true,
        },
        cellRenderer: "dateCell",
        width: 160,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
        sort: "desc",
      },
      {
        headerName: "By",
        field: "EnteredBy",
        width: 120,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Note",
        field: "Notes",
        width: 425,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
    ];

    this.frameworkComponents = {
      dateCell: datetime,
    };
    this.loadReport();
  },
  methods: {
    onGridReady() {},
    toggleAddNoteSlideover(size) {
      this.slideoverVisibleAddNote = !this.slideoverVisibleAddNote;
      this.size = size;
    },
    buildNote(note) {
      let adjustmentType = "";
      let adjustmentArray = [];
      if (note.reason?.includes("Edit Items")) {
        adjustmentType = "-";
      }
      adjustmentArray.push(
        `Reason: ${note.reason}, Policy: ${note.policy}, Action: ${
          note.action
        }, Amount: ${moneyFormatter.format(note.amount)}`
      );

      if (note?.adjustments_details?.length > 0) {
        let counter = 1;
        let adjustmentSkuArray = [];
        for (let item of note.adjustments_details) {
          adjustmentSkuArray.push(
            `(${counter}) SKU: ${item.sku} Quantity: ${adjustmentType}${item.quantity}`
          );
          counter++;
        }
        adjustmentArray.push(` ${adjustmentSkuArray.join(", ")}`);
      }

      note.value ? adjustmentArray.push(` Comment: ${note.value}`) : "";
      note.additional_info
        ? adjustmentArray.push(` Additional Info: ${note.additional_info}`)
        : "";
      note.Notes = `${adjustmentArray.join(", ")}`;
      return note.Notes;
    },
    onNoteSelectionChanged() {
      const selectedRows = this.gridOptions.api.getSelectedRows();
      this.selectedNote =
        selectedRows[0] !== undefined ? selectedRows[0] : false;
    },
    loadReport() {
      this.selectedNote = false;
      this.gridApi?.showLoadingOverlay();

      NotesApi.getNotesByOrderNumber(this.orderNumber)
        .then((results) => {
          this.notes = results.data.body;
          this.$store.commit("orders/SET_ORDER_NOTES", this.notes);
        })
        .finally(() => {
          this.rowData = this.filteredNotes;
          this.gridOptions.api.hideOverlay();
        });
    },
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  filters: {},
};
</script>
<style scoped lang="scss">
@import "../../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
